import * as clack from "@clack/prompts";
import pc from "picocolors";
import { getAppBaseUrl } from "./config.js";
import { isJsonMode } from "./json-output.js";

/**
 * Deployment progress tracker with spinners using clack.
 * Automatically suppresses all visual output in JSON mode
 * while still executing the underlying work.
 */
export class DeploymentProgress {
  private currentSpinner: ReturnType<typeof clack.spinner> | null = null;

  private get silent(): boolean {
    return isJsonMode();
  }

  /**
   * Start a spinner with a message
   */
  start(message: string) {
    if (this.silent) {
      return;
    }
    if (this.currentSpinner) {
      this.currentSpinner.stop("");
      this.currentSpinner = null;
    }
    this.currentSpinner = clack.spinner();
    this.currentSpinner.start(message);
  }

  /**
   * Mark current step as succeeded
   */
  succeed(message: string) {
    if (this.silent) {
      return;
    }
    if (this.currentSpinner) {
      this.currentSpinner.stop("");
      this.currentSpinner = null;
    }
    clack.log.success(message);
  }

  /**
   * Mark current step as failed
   */
  fail(message: string) {
    if (this.silent) {
      return;
    }
    if (this.currentSpinner) {
      this.currentSpinner.stop("");
      this.currentSpinner = null;
    }
    clack.log.error(message);
  }

  /**
   * Show info message
   */
  info(message: string) {
    if (this.silent) {
      return;
    }
    clack.log.info(message);
  }

  /**
   * Show step message
   */
  step(message: string) {
    if (this.silent) {
      return;
    }
    clack.log.step(message);
  }

  /**
   * Execute a step with automatic spinner handling.
   * Always runs the function; only suppresses visual output in JSON mode.
   */
  async execute<T>(message: string, fn: () => Promise<T>): Promise<T> {
    if (this.silent) {
      return fn();
    }
    this.start(message);
    try {
      const result = await fn();
      this.succeed(message);
      return result;
    } catch (error) {
      this.fail(message);
      throw error;
    }
  }

  /**
   * Stop the spinner
   */
  stop(message?: string) {
    if (this.silent) {
      return;
    }
    if (this.currentSpinner) {
      this.currentSpinner.stop(message || "");
    }
  }
}

/**
 * DNS record type
 */
export type DNSRecord = {
  name: string;
  type: string;
  value: string;
};

/**
 * Success output configuration
 */
export type SuccessOutputs = {
  roleArn: string;
  configSetName?: string;
  region: string;
  dnsRecords?: DNSRecord[];
  trackingDomainDnsRecords?: DNSRecord[];
  acmValidationRecords?: DNSRecord[];
  tableName?: string;
  dnsAutoCreated?: boolean;
  dnsProvider?: "route53" | "vercel" | "cloudflare" | "manual";
  domain?: string;
  customTrackingDomain?: string;
  httpsTrackingEnabled?: boolean;
  mailFromDomain?: string;
  /** This deployment's own dashboard — see resolveDashboardUrl. */
  dashboardUrl?: string;
};

/**
 * Display success message with infrastructure outputs
 */
export function displaySuccess(outputs: SuccessOutputs) {
  const lines = [
    "",
    pc.bold("Role ARN:"),
    `  ${pc.cyan(outputs.roleArn)}`,
    "",
    `${pc.bold("Region:")} ${pc.cyan(outputs.region)}`,
  ];

  if (outputs.configSetName) {
    lines.push(`${pc.bold("Config Set:")} ${pc.cyan(outputs.configSetName)}`);
  }

  if (outputs.tableName) {
    lines.push(`${pc.bold("DynamoDB Table:")} ${pc.cyan(outputs.tableName)}`);
  }

  lines.push(
    "",
    pc.bold("Next steps:"),
    `  1. Install SDK: ${pc.yellow("npm install @wraps.dev/email")}`,
    ""
  );

  // Embed ready-to-run SDK snippet with actual deployment values
  if (outputs.domain) {
    lines.push(
      pc.bold("  Quick start:"),
      "",
      pc.dim("    import { WrapsEmail } from '@wraps.dev/email';"),
      pc.dim(""),
      pc.dim("    const email = new WrapsEmail({"),
      pc.dim(`      roleArn: '${outputs.roleArn}',`),
      pc.dim(`      region: '${outputs.region}'`),
      pc.dim("    });"),
      pc.dim(""),
      pc.dim("    await email.send({"),
      pc.dim(`      from: 'hello@${outputs.domain}',`),
      pc.dim("      to: 'user@example.com',"),
      pc.dim("      subject: 'Hello from Wraps!',"),
      pc.dim("      html: '<h1>It works!</h1>'"),
      pc.dim("    });"),
      ""
    );
  }

  lines.push(
    `  2. View dashboard: ${pc.blue(outputs.dashboardUrl ?? getAppBaseUrl())}`,
    ""
  );

  clack.outro(pc.green("Email infrastructure deployed successfully!"));
  console.log(lines.join("\n"));

  // Show DNS auto-creation message
  if (outputs.dnsAutoCreated && outputs.domain) {
    const providerNames: Record<string, string> = {
      route53: "AWS Route53",
      vercel: "Vercel DNS",
      cloudflare: "Cloudflare",
      manual: "Manual",
    };
    const providerDisplay =
      providerNames[outputs.dnsProvider ?? "route53"] ?? "your DNS provider";
    clack.note(
      `DNS records (DKIM, SPF, DMARC) were automatically created in ${providerDisplay} for ${pc.cyan(
        outputs.domain
      )}.\n\nVerification should complete within a few minutes.`,
      pc.green("✓ DNS Auto-Configured")
    );
  }

  if (outputs.dnsRecords && outputs.dnsRecords.length > 0) {
    // Extract domain from first DKIM record
    const domain = outputs.dnsRecords[0]?.name.split("._domainkey.")[1];

    const dnsLines = [
      pc.bold("DKIM Records (CNAME):"),
      ...outputs.dnsRecords.map(
        (record) =>
          `  ${pc.cyan(record.name)} ${pc.dim(record.type)} "${record.value}"`
      ),
    ];

    if (domain) {
      // Use MAIL FROM domain for DMARC rua if configured, otherwise use main domain
      const dmarcRuaDomain = outputs.mailFromDomain || domain;
      dnsLines.push(
        "",
        pc.bold("SPF Record (TXT):"),
        `  ${pc.cyan(domain)} ${pc.dim("TXT")} "v=spf1 include:amazonses.com ~all"`,
        pc.dim(
          "  Note: If you have an existing SPF record, add 'include:amazonses.com' to it"
        ),
        "",
        pc.bold("DMARC Record (TXT):"),
        `  ${pc.cyan(`_dmarc.${domain}`)} ${pc.dim("TXT")} "v=DMARC1; p=quarantine; sp=quarantine; np=reject; rua=mailto:postmaster@${dmarcRuaDomain}"`
      );

      // Add MAIL FROM domain DNS records if configured
      if (outputs.mailFromDomain) {
        dnsLines.push(
          "",
          pc.bold("MAIL FROM Domain Records (for DMARC alignment):"),
          `  ${pc.cyan(outputs.mailFromDomain)} ${pc.dim("MX")} "10 feedback-smtp.${outputs.region}.amazonses.com"`,
          `  ${pc.cyan(outputs.mailFromDomain)} ${pc.dim("TXT")} "v=spf1 include:amazonses.com ~all"`
        );
      }
    }

    clack.note(dnsLines.join("\n"), "DNS Records to add:");
  }

  // Show ACM certificate validation records if HTTPS tracking is enabled
  if (outputs.acmValidationRecords && outputs.acmValidationRecords.length > 0) {
    const acmDnsLines = [
      pc.bold("SSL Certificate Validation (ACM):"),
      ...outputs.acmValidationRecords.map(
        (record) =>
          `  ${pc.cyan(record.name)} ${pc.dim(record.type)} "${record.value}"`
      ),
      "",
      pc.dim(
        "Note: These records are required to validate your SSL certificate."
      ),
      pc.dim(
        "CloudFront will be enabled automatically after certificate validation."
      ),
    ];

    clack.note(
      acmDnsLines.join("\n"),
      "SSL Certificate Validation DNS Records:"
    );
  }

  // Show tracking domain DNS records if custom tracking domain is configured
  if (
    outputs.trackingDomainDnsRecords &&
    outputs.trackingDomainDnsRecords.length > 0
  ) {
    const trackingProtocol = outputs.httpsTrackingEnabled ? "HTTPS" : "HTTP";
    const trackingDnsLines = [
      pc.bold(`Custom Tracking Domain - ${trackingProtocol} Redirect CNAME:`),
      ...outputs.trackingDomainDnsRecords.map(
        (record) =>
          `  ${pc.cyan(record.name)} ${pc.dim(record.type)} "${record.value}"`
      ),
      "",
      pc.dim(
        "Note: This CNAME allows SES to rewrite links in your emails to use"
      ),
      pc.dim("your custom domain for open and click tracking."),
    ];

    if (outputs.httpsTrackingEnabled) {
      trackingDnsLines.push(
        "",
        pc.dim("HTTPS tracking is enabled via CloudFront with SSL certificate.")
      );
    }

    clack.note(
      trackingDnsLines.join("\n"),
      "Custom Tracking Domain DNS Records:"
    );

    if (outputs.customTrackingDomain) {
      console.log(
        `\n${pc.dim("Run:")} ${pc.yellow(`wraps email verify --domain ${outputs.customTrackingDomain}`)} ${pc.dim(
          "(after DNS propagates)"
        )}\n`
      );
    }
  }

  // Show tracking domain separately if we only have tracking domain (no other DNS records)
  // ONLY for HTTP tracking - HTTPS tracking DNS records are shown after CloudFront is created
  if (
    outputs.customTrackingDomain &&
    !outputs.httpsTrackingEnabled && // Only show for HTTP tracking
    !outputs.dnsAutoCreated &&
    (!outputs.dnsRecords || outputs.dnsRecords.length === 0) &&
    (!outputs.trackingDomainDnsRecords ||
      outputs.trackingDomainDnsRecords.length === 0)
  ) {
    const trackingLines = [
      pc.bold("Tracking Domain (CNAME):"),
      `  ${pc.cyan(outputs.customTrackingDomain)} ${pc.dim("CNAME")} "r.${outputs.region}.awstrack.me"`,
      "",
      pc.dim(
        "Note: This CNAME allows SES to rewrite links in your emails to use"
      ),
      pc.dim("your custom domain for open and click tracking."),
    ];

    clack.note(trackingLines.join("\n"), "DNS Record to add:");
  }
}

/**
 * Status output configuration
 */
export type StatusOutputs = {
  integrationLevel: "dashboard-only" | "enhanced";
  region: string;
  domains: Array<{
    domain: string;
    status: "verified" | "pending" | "failed";
    dkimTokens?: string[];
    mailFromDomain?: string;
    mailFromStatus?: string;
    /** Whether this domain is tracked in wraps metadata */
    managed?: boolean;
    /** Whether this is the primary Pulumi-managed domain */
    isPrimary?: boolean;
    /** Purpose label from metadata (transactional, marketing, etc.) */
    purpose?: string;
    /** Per-domain tracking config (opens/clicks) */
    trackingConfig?: { opens: boolean; clicks: boolean };
  }>;
  resources: {
    roleArn?: string;
    configSetName?: string;
    tableName?: string;
    lambdaFunctions?: number;
    snsTopics?: number;
    archiveArn?: string;
    archivingEnabled?: boolean;
    archiveRetention?: string;
  };
  tracking?: {
    customTrackingDomain?: string;
    httpsEnabled?: boolean;
    cloudFrontDomain?: string;
  };
  /** This deployment's own dashboard — see resolveDashboardUrl. */
  dashboardUrl?: string;
  /**
   * SES account-level sending state. Optional because a status run that could
   * not reach SES must report absence, never a fabricated "not sandboxed".
   */
  sending?: {
    sandbox: boolean;
    /** True when SES could not be read — do not present `sandbox` as fact. */
    sandboxUncertain: boolean;
    max24HourSend?: number;
    maxSendRate?: number;
    sentLast24Hours?: number;
    enforcementStatus?: string;
  };
  /**
   * Names of live CloudFormation stacks that look like Wraps stacks, found
   * when no Pulumi stack exists. Only populated on the dashboard-only /
   * CloudFormation-owned path — see email/status.ts.
   */
  cloudFormationStacks?: string[];
};

/**
 * Display status information
 */
export function displayStatus(status: StatusOutputs) {
  clack.intro(pc.bold("Wraps Email Infrastructure"));

  const infoLines = [
    `${pc.bold("Integration:")} ${pc.cyan(status.integrationLevel)}`,
    `${pc.bold("Region:")} ${pc.cyan(status.region)}`,
  ];

  if (status.sending) {
    const { sandbox, sandboxUncertain } = status.sending;
    // Never present "production" when SES could not be reached — an
    // unreachable account is not the same thing as a verified one.
    if (sandboxUncertain) {
      infoLines.push(
        `${pc.bold("Sending:")} ${pc.yellow("could not determine (SES unreachable)")}`
      );
    } else if (sandbox) {
      infoLines.push(
        `${pc.bold("Sending:")} ${pc.yellow("SANDBOX")} ${pc.dim("— you can only send to verified addresses")}`
      );
    } else {
      infoLines.push(`${pc.bold("Sending:")} ${pc.green("production")}`);
    }

    if (
      status.sending.sentLast24Hours !== undefined ||
      status.sending.max24HourSend !== undefined ||
      status.sending.maxSendRate !== undefined
    ) {
      infoLines.push(
        `${pc.bold("Quota:")} ${pc.cyan(`${status.sending.sentLast24Hours ?? 0}/${status.sending.max24HourSend ?? 0}`)} sent (24h) ${pc.dim(`· max rate ${status.sending.maxSendRate ?? 0}/s`)}`
      );
    }
  }

  if (status.cloudFormationStacks && status.cloudFormationStacks.length > 0) {
    infoLines.push(
      `${pc.bold("CloudFormation stacks:")} ${pc.cyan(status.cloudFormationStacks.join(", "))}`
    );
  }

  if (status.domains.length > 0) {
    const PURPOSE_DISPLAY: Record<string, string> = {
      transactional: "Transactional",
      marketing: "Marketing",
      notifications: "Notifications",
      other: "General",
    };

    const domainStrings = status.domains.map((d) => {
      const statusIcon =
        d.status === "verified" ? "✓" : d.status === "pending" ? "⏱" : "✗";
      const statusColor =
        d.status === "verified"
          ? pc.green
          : d.status === "pending"
            ? pc.yellow
            : pc.red;

      // Build label: "Primary" for primary, purpose for additional, empty for unmanaged
      let label = "";
      if (d.isPrimary) {
        label = pc.dim(" (Primary)");
      } else if (d.managed && d.purpose) {
        label = pc.dim(` (${PURPOSE_DISPLAY[d.purpose] || d.purpose})`);
      }

      let domainLine = `    ${d.domain}${label} ${statusColor(`${statusIcon} ${d.status}`)}`;

      // Add MAIL FROM domain info if configured
      if (d.mailFromDomain) {
        const mailFromStatusIcon = d.mailFromStatus === "SUCCESS" ? "✓" : "⏱";
        const mailFromColor =
          d.mailFromStatus === "SUCCESS" ? pc.green : pc.yellow;
        domainLine += `\n      ${pc.dim("MAIL FROM:")} ${d.mailFromDomain} ${mailFromColor(mailFromStatusIcon)}`;
      }

      if (d.trackingConfig !== undefined) {
        const opensLabel = d.trackingConfig.opens
          ? pc.green("on")
          : pc.dim("off");
        const clicksLabel = d.trackingConfig.clicks
          ? pc.green("on")
          : pc.dim("off");
        domainLine += `\n      ${pc.dim("Tracking:")} opens ${opensLabel} · clicks ${clicksLabel}`;
      }

      return domainLine;
    });
    infoLines.push(`${pc.bold("Domains:")}\n${domainStrings.join("\n")}`);
  }

  clack.note(infoLines.join("\n"), "Configuration");

  // Features
  const featureLines = [];
  featureLines.push(`  ${pc.green("✓")} Email Sending ${pc.dim("(via SES)")}`);

  if (status.resources.tableName) {
    featureLines.push(
      `  ${pc.green("✓")} Email Tracking ${pc.dim("(DynamoDB logs)")}`
    );
  } else {
    featureLines.push(
      `  ${pc.dim("○")} Email Tracking ${pc.dim("(run 'wraps email upgrade' to enable)")}`
    );
  }

  if (
    status.resources.lambdaFunctions &&
    status.resources.lambdaFunctions > 0
  ) {
    featureLines.push(
      `  ${pc.green("✓")} Bounce/Complaint Handling ${pc.dim("(automated)")}`
    );
  } else {
    featureLines.push(
      `  ${pc.dim("○")} Bounce/Complaint Handling ${pc.dim("(run 'wraps email upgrade' to enable)")}`
    );
  }

  // Email Archiving
  if (status.resources.archivingEnabled) {
    const retentionLabel =
      {
        "7days": "7 days",
        "30days": "30 days",
        "90days": "90 days",
        "6months": "6 months",
        "1year": "1 year",
        "18months": "18 months",
      }[status.resources.archiveRetention || "90days"] || "90 days";
    featureLines.push(
      `  ${pc.green("✓")} Email Archiving ${pc.dim(`(${retentionLabel} retention)`)}`
    );
  } else {
    featureLines.push(
      `  ${pc.dim("○")} Email Archiving ${pc.dim("(run 'wraps email upgrade' to enable)")}`
    );
  }

  // Custom Tracking Domain
  if (status.tracking?.customTrackingDomain) {
    const protocol = status.tracking.httpsEnabled ? "HTTPS" : "HTTP";
    const cloudFrontStatus = status.tracking.httpsEnabled
      ? status.tracking.cloudFrontDomain
        ? pc.green("✓ Active")
        : pc.yellow("⏱ Pending")
      : "";
    const trackingLabel = status.tracking.httpsEnabled
      ? `${protocol} tracking ${cloudFrontStatus}`
      : `${protocol} tracking`;
    featureLines.push(
      `  ${pc.green("✓")} Custom Tracking Domain ${pc.dim(`(${trackingLabel})`)}`
    );
    featureLines.push(`      ${pc.cyan(status.tracking.customTrackingDomain)}`);
  } else {
    featureLines.push(
      `  ${pc.dim("○")} Custom Tracking Domain ${pc.dim("(run 'wraps email upgrade' to enable)")}`
    );
  }

  featureLines.push(
    `  ${pc.green("✓")} Console Dashboard ${pc.dim("(run 'wraps console')")}`
  );

  clack.note(featureLines.join("\n"), "Features");

  // Resources
  const resourceLines = [];

  if (status.resources.roleArn) {
    resourceLines.push(
      `  ${pc.green("✓")} IAM Role: ${pc.cyan(status.resources.roleArn)}`
    );
  }

  if (status.resources.configSetName) {
    resourceLines.push(
      `  ${pc.green("✓")} Configuration Set: ${pc.cyan(status.resources.configSetName)}`
    );
  }

  if (status.resources.tableName) {
    resourceLines.push(
      `  ${pc.green("✓")} DynamoDB Table: ${pc.cyan(status.resources.tableName)}`
    );
  }

  if (status.resources.lambdaFunctions) {
    resourceLines.push(
      `  ${pc.green("✓")} Lambda Functions: ${pc.cyan(
        `${status.resources.lambdaFunctions} deployed`
      )}`
    );
  }

  if (status.resources.snsTopics) {
    resourceLines.push(
      `  ${pc.green("✓")} SNS Topics: ${pc.cyan(`${status.resources.snsTopics} configured`)}`
    );
  }

  if (status.resources.archiveArn) {
    resourceLines.push(
      `  ${pc.green("✓")} Mail Manager Archive: ${pc.cyan(status.resources.archiveArn)}`
    );
  }

  clack.note(resourceLines.join("\n"), "Resources");

  // Show DNS records for pending domains OR domains with pending MAIL FROM
  const domainsNeedingDNS = status.domains.filter(
    (d) =>
      (d.status === "pending" && d.dkimTokens) ||
      (d.mailFromDomain && d.mailFromStatus !== "SUCCESS")
  );
  if (domainsNeedingDNS.length > 0) {
    for (const domain of domainsNeedingDNS) {
      const dnsLines = [];

      // DKIM records (only for pending domains)
      if (
        domain.status === "pending" &&
        domain.dkimTokens &&
        domain.dkimTokens.length > 0
      ) {
        // Use MAIL FROM domain for DMARC rua if configured, otherwise use main domain
        const dmarcRuaDomain = domain.mailFromDomain || domain.domain;
        dnsLines.push(
          pc.bold("DKIM Records (CNAME):"),
          ...domain.dkimTokens.map(
            (token) =>
              `  ${pc.cyan(`${token}._domainkey.${domain.domain}`)} ${pc.dim("CNAME")} "${token}.dkim.amazonses.com"`
          ),
          "",
          pc.bold("SPF Record (TXT):"),
          `  ${pc.cyan(domain.domain)} ${pc.dim("TXT")} "v=spf1 include:amazonses.com ~all"`,
          pc.dim(
            "  Note: If you have an existing SPF record, add 'include:amazonses.com' to it"
          ),
          "",
          pc.bold("DMARC Record (TXT):"),
          `  ${pc.cyan(`_dmarc.${domain.domain}`)} ${pc.dim("TXT")} "v=DMARC1; p=quarantine; sp=quarantine; np=reject; rua=mailto:postmaster@${dmarcRuaDomain}"`
        );
      }

      // MAIL FROM records (if configured but not verified)
      if (domain.mailFromDomain && domain.mailFromStatus !== "SUCCESS") {
        if (dnsLines.length > 0) {
          dnsLines.push("");
        }
        dnsLines.push(
          pc.bold("MAIL FROM Domain Records (for DMARC alignment):"),
          `  ${pc.cyan(domain.mailFromDomain)} ${pc.dim("MX")} "10 feedback-smtp.${status.region}.amazonses.com"`,
          `  ${pc.cyan(domain.mailFromDomain)} ${pc.dim("TXT")} "v=spf1 include:amazonses.com ~all"`
        );
      }

      if (dnsLines.length > 0) {
        clack.note(dnsLines.join("\n"), `DNS Records for ${domain.domain}`);
      }
    }

    // Show verify command with first domain needing DNS as example
    const exampleDomain = domainsNeedingDNS[0].domain;
    console.log(
      `\n${pc.dim("Run:")} ${pc.yellow(`wraps email verify --domain ${exampleDomain}`)} ${pc.dim(
        "(after DNS propagates)"
      )}\n`
    );
  }

  console.log(
    `\n${pc.bold("Dashboard:")} ${pc.blue(status.dashboardUrl ?? getAppBaseUrl())}`
  );
  console.log(`${pc.bold("Docs:")} ${pc.blue("https://wraps.dev/docs")}\n`);
}

/**
 * Operation type for resource changes
 */
export type ResourceOperation =
  | "create"
  | "update"
  | "delete"
  | "replace"
  | "same";

/**
 * Individual resource change information
 */
export type ResourceChange = {
  name: string;
  type: string;
  operation: ResourceOperation;
  diffs?: string[];
};

/**
 * Preview output configuration
 */
export type PreviewOutputs = {
  changeSummary: {
    create?: number;
    update?: number;
    delete?: number;
    same?: number;
    replace?: number;
  };
  resourceChanges?: ResourceChange[];
  costEstimate?: string;
  commandName: string;
};

/**
 * Format AWS resource type to human-readable name
 */
function formatResourceType(type: string): string {
  // Common AWS resource type mappings
  const typeMap: Record<string, string> = {
    "aws:iam/role:Role": "IAM Role",
    "aws:iam/policy:Policy": "IAM Policy",
    "aws:iam/rolePolicyAttachment:RolePolicyAttachment":
      "IAM Policy Attachment",
    "aws:iam/openIdConnectProvider:OpenIdConnectProvider": "OIDC Provider",
    "aws:ses/configurationSet:ConfigurationSet": "SES Configuration Set",
    "aws:ses/emailIdentity:EmailIdentity": "SES Email Identity",
    "aws:ses/eventDestination:EventDestination": "SES Event Destination",
    "aws:sesv2/configurationSetEventDestination:ConfigurationSetEventDestination":
      "SES Event Destination",
    "aws:dynamodb/table:Table": "DynamoDB Table",
    "aws:lambda/function:Function": "Lambda Function",
    "aws:lambda/eventSourceMapping:EventSourceMapping": "Lambda Event Source",
    "aws:sqs/queue:Queue": "SQS Queue",
    "aws:cloudwatch/eventRule:EventRule": "EventBridge Rule",
    "aws:cloudwatch/eventTarget:EventTarget": "EventBridge Target",
    "aws:sns/topic:Topic": "SNS Topic",
    "aws:sns/topicSubscription:TopicSubscription": "SNS Subscription",
    "aws:s3/bucket:Bucket": "S3 Bucket",
    "aws:s3/bucketPolicy:BucketPolicy": "S3 Bucket Policy",
    "aws:cloudfront/distribution:Distribution": "CloudFront Distribution",
    "aws:acm/certificate:Certificate": "ACM Certificate",
    "aws:acm/certificateValidation:CertificateValidation": "ACM Validation",
    "aws:route53/record:Record": "Route53 Record",
    "pulumi:pulumi:Stack": "Pulumi Stack",
  };

  return typeMap[type] || type.split(":").pop() || type;
}

/**
 * Get icon for operation type
 */
function getOperationIcon(operation: ResourceOperation): string {
  switch (operation) {
    case "create":
      return pc.green("+");
    case "update":
      return pc.yellow("~");
    case "delete":
      return pc.red("-");
    case "replace":
      return pc.magenta("±");
    case "same":
      return pc.dim("=");
    default:
      return " ";
  }
}

/**
 * Get operation label with color
 */
function getOperationLabel(operation: ResourceOperation): string {
  switch (operation) {
    case "create":
      return pc.green("CREATE");
    case "update":
      return pc.yellow("UPDATE");
    case "delete":
      return pc.red("DELETE");
    case "replace":
      return pc.magenta("REPLACE");
    case "same":
      return pc.dim("UNCHANGED");
  }
}

/**
 * Display preview results with resource changes and cost estimate
 */
export function displayPreview(outputs: PreviewOutputs): void {
  console.log(pc.yellow("\n━━━ PREVIEW MODE (no changes will be made) ━━━\n"));

  // If we have detailed resource changes, show them grouped by operation
  if (outputs.resourceChanges && outputs.resourceChanges.length > 0) {
    // Group resources by operation
    const grouped = new Map<ResourceOperation, ResourceChange[]>();
    for (const resource of outputs.resourceChanges) {
      const existing = grouped.get(resource.operation) || [];
      existing.push(resource);
      grouped.set(resource.operation, existing);
    }

    // Order: create, update, replace, delete, same
    const operationOrder: ResourceOperation[] = [
      "create",
      "update",
      "replace",
      "delete",
      "same",
    ];

    const sections: string[] = [];

    for (const operation of operationOrder) {
      const resources = grouped.get(operation);
      if (!resources || resources.length === 0) {
        continue;
      }

      // Skip showing "same" resources unless there are few total changes
      if (operation === "same" && outputs.resourceChanges.length > 10) {
        sections.push(
          `${getOperationLabel(operation)} ${pc.dim(`(${resources.length} resources)`)}`
        );
        continue;
      }

      const resourceLines = resources
        .map((r) => {
          const icon = getOperationIcon(operation);
          const typeLabel = pc.dim(`(${formatResourceType(r.type)})`);
          let line = `  ${icon} ${r.name} ${typeLabel}`;

          // Show diffs for updates
          if (r.diffs && r.diffs.length > 0 && operation === "update") {
            const diffStr = r.diffs.slice(0, 3).join(", ");
            const more =
              r.diffs.length > 3 ? ` +${r.diffs.length - 3} more` : "";
            line += `\n      ${pc.dim(`changed: ${diffStr}${more}`)}`;
          }

          return line;
        })
        .join("\n");

      sections.push(
        `${getOperationLabel(operation)} ${pc.dim(`(${resources.length})`)}\n${resourceLines}`
      );
    }

    if (sections.length > 0) {
      console.log(sections.join("\n\n"));
      console.log();
    }
  } else {
    // Fallback to summary-only view
    const changes = outputs.changeSummary;
    const summaryLines: string[] = [];

    if (changes.create && changes.create > 0) {
      summaryLines.push(`  ${pc.green("+")} ${changes.create} to create`);
    }
    if (changes.update && changes.update > 0) {
      summaryLines.push(`  ${pc.yellow("~")} ${changes.update} to update`);
    }
    if (changes.delete && changes.delete > 0) {
      summaryLines.push(`  ${pc.red("-")} ${changes.delete} to destroy`);
    }
    if (changes.same && changes.same > 0) {
      summaryLines.push(`  ${pc.dim("=")} ${changes.same} unchanged`);
    }
    if (changes.replace && changes.replace > 0) {
      summaryLines.push(`  ${pc.magenta("±")} ${changes.replace} to replace`);
    }

    if (summaryLines.length > 0) {
      clack.note(summaryLines.join("\n"), "Resource Changes");
    } else {
      clack.note("No changes detected", "Resource Changes");
    }
  }

  // Display cost estimate
  if (outputs.costEstimate) {
    clack.note(outputs.costEstimate, "Estimated Monthly Cost");
  }

  console.log(pc.yellow("━━━ END PREVIEW (no changes were made) ━━━\n"));
}
